import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ShieldCheck, Clock, Truck, Factory } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import Layout from "../Layout";
import SEOHead from "../SEOHead";
import Breadcrumb from "../Breadcrumb";
import FAQSection from "../FAQSection";
import { states, getCitiesByState } from "../../data/locations";
import { business } from "../../config/business";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const process = [
  { icon: ShieldCheck, title: "Design at Our Facility", text: "Every structure engineered to your site's specific span, height and load requirements." },
  { icon: Factory, title: "Factory Fabrication", text: "Steel fabricated under controlled shop conditions in Hyderabad." },
  { icon: Truck, title: "Transport", text: "Fabricated members delivered to your project site." },
  { icon: Clock, title: "On-Site Erection", text: "Assembly managed through to a handover-ready structure." },
];

const faqs = [
  { q: "Does TR Structures have branch offices in every city listed?", a: "No — we operate from a single facility in Bibinagar, Hyderabad. We design and fabricate there, then deliver, transport and erect at project sites across Telangana, Andhra Pradesh and Karnataka." },
  { q: "Do you serve locations not listed here?", a: "The cities listed reflect our primary service focus, but we evaluate projects on a case-by-case basis — get in touch even if your location isn't listed." },
];

const LocationsHub = () => {
  return (
    <Layout>
      <SEOHead
        title="Service Locations | TR Structures — Telangana, Andhra Pradesh & Karnataka"
        description="TR Structures designs and fabricates PEB buildings, industrial sheds and steel structures from our Hyderabad facility, delivering across Telangana, Andhra Pradesh and Karnataka."
        keywords="PEB manufacturer Telangana, PEB manufacturer Andhra Pradesh, PEB manufacturer Karnataka, industrial shed locations, TR Structures service areas"
      />
      <Breadcrumb items={[{ name: "Locations" }]} />

      {/* 1. Hero */}
      <section className="section-padding pb-8">
        <div className="container-custom">
          <h1 className="font-montserrat text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 max-w-3xl">
            Where We <span className="text-secondary">Build</span>
          </h1>
          <p className="text-muted-foreground max-w-3xl mb-8 text-lg">
            TR Structures is based in Bibinagar, Hyderabad, where we design and fabricate every
            structure. We deliver, transport and erect PEB buildings, industrial sheds and steel
            structures on-site across Telangana, Andhra Pradesh and Karnataka.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/enquiry" className="btn-primary inline-block px-8 py-4 text-center">
              Get Project Estimate
            </Link>
            <a href={business.whatsapp.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-8 py-4 rounded-lg font-montserrat font-semibold hover:bg-[#20bd5a] transition">
              <FaWhatsapp size={20} aria-hidden="true" /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      {/* 2. Trust strip */}
      <section className="bg-primary py-6">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-primary-foreground text-center">
            <span className="font-montserrat font-semibold flex items-center gap-2"><MapPin size={18} aria-hidden="true" /> 3 states, 16 cities served</span>
            <span className="font-montserrat font-semibold flex items-center gap-2"><ShieldCheck size={18} aria-hidden="true" /> Site-specific structural design</span>
            <span className="font-montserrat font-semibold flex items-center gap-2"><Truck size={18} aria-hidden="true" /> End-to-end delivery & erection</span>
          </div>
        </div>
      </section>

      {/* 3-4. States + cities */}
      <section className="section-padding">
        <div className="container-custom">
          {states.map((state) => {
            const cities = getCitiesByState(state.slug);
            return (
              <div key={state.slug} className="mb-16 last:mb-0">
                <Link
                  to={`/locations/${state.slug}`}
                  className="inline-flex items-center gap-2 font-montserrat text-2xl font-bold text-foreground mb-2 hover:text-primary transition"
                >
                  <MapPin className="text-secondary" size={24} aria-hidden="true" />
                  {state.name}
                </Link>
                <p className="text-muted-foreground mb-6 max-w-2xl">{state.heroNote}</p>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.1 }}
                  transition={{ staggerChildren: 0.05 }}
                  className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
                >
                  {cities.map((city) => (
                    <motion.div key={city.slug} variants={fadeUp}>
                      <Link
                        to={`/locations/${state.slug}/${city.slug}`}
                        className="block p-5 bg-card rounded-xl shadow-md card-hover border border-border"
                      >
                        <h3 className="font-montserrat font-bold text-foreground mb-1">{city.name}</h3>
                        <p className="text-sm text-muted-foreground">{city.distance}</p>
                      </Link>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 5. Process */}
      <section className="section-padding bg-muted">
        <div className="container-custom">
          <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-foreground mb-10 text-center">
            How We Deliver to Every Location
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((step, i) => (
              <div key={step.title} className="p-6 bg-card rounded-xl shadow-md text-center card-hover">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <step.icon className="text-primary" size={22} aria-hidden="true" />
                </div>
                <p className="text-xs font-montserrat font-bold text-secondary mb-2">STEP {i + 1}</p>
                <h3 className="font-montserrat font-bold text-foreground mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <FAQSection faqs={faqs} />

      {/* 7. CTA */}
      <section className="section-padding bg-secondary">
        <div className="container-custom text-center">
          <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-secondary-foreground mb-4">
            Get a Quote for Your Location
          </h2>
          <p className="text-secondary-foreground/90 mb-8 max-w-xl mx-auto">
            Tell us where and what you're building — we'll follow up with a design consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/enquiry" className="btn-primary inline-block px-8 py-4">
              Get Project Estimate
            </Link>
            <a href={business.whatsapp.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-8 py-4 rounded-lg font-montserrat font-semibold hover:bg-[#20bd5a] transition">
              <FaWhatsapp size={20} aria-hidden="true" /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default LocationsHub;
